import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { AlertCircle, Check, ChevronDown } from "lucide-react";

export function FieldLabel({ children, required, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="block font-heading font-semibold text-slate-900 text-base mb-2">
      {children} {required && <span className="text-red-600">*</span>}
    </label>
  );
}

export function ErrorText({ error }) {
  if (!error) return null;
  return (
    <p className="mt-1.5 flex items-center gap-1.5 text-sm text-red-600" data-testid="field-error">
      <AlertCircle className="h-4 w-4 shrink-0" /> {error}
    </p>
  );
}

export function TextField({ label, required, value, onChange, error, type = "text", placeholder, testId, hint }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      {hint && <p className="text-sm text-slate-500 -mt-1 mb-2">{hint}</p>}
      <input
        type={type}
        inputMode={type === "number" ? "decimal" : type === "tel" ? "tel" : "text"}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`w-full h-14 px-4 text-lg rounded-lg border bg-white outline-none transition-colors focus:ring-4 focus:ring-orange-200 focus:border-orange-600 ${
          error ? "border-red-500" : "border-slate-300"
        }`}
      />
      <ErrorText error={error} />
    </div>
  );
}

export function RadioCards({ label, required, options, value, onChange, error, testId }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="grid gap-3">
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              type="button"
              key={opt}
              onClick={() => onChange(opt)}
              data-testid={`${testId}-${opt}`}
              className={`w-full min-h-[56px] px-4 py-3 rounded-lg border-2 text-left text-lg font-medium transition-colors flex items-center gap-3 ${
                active ? "border-orange-600 bg-orange-50 text-orange-800" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
              }`}
            >
              <span className={`h-5 w-5 rounded-full border-2 shrink-0 flex items-center justify-center ${active ? "border-orange-600" : "border-slate-400"}`}>
                {active && <span className="h-2.5 w-2.5 rounded-full bg-orange-600" />}
              </span>
              {opt}
            </button>
          );
        })}
      </div>
      <ErrorText error={error} />
    </div>
  );
}

export function Dropdown({ label, required, options, value, onChange, error, testId, placeholder = "Seleccione una opción" }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testId}
          className={`w-full h-14 px-4 text-lg rounded-lg bg-white ${error ? "border-red-500" : "border-slate-300"}`}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-white max-h-72">
          {options.map((opt) => (
            <SelectItem key={opt} value={opt} className="text-base py-3" data-testid={`${testId}-opt-${opt}`}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ErrorText error={error} />
    </div>
  );
}

export function SearchableDropdown({ label, required, options, value, onChange, error, testId, placeholder = "Seleccione", searchPlaceholder = "Escriba para buscar…" }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            className={`w-full h-14 px-4 text-lg rounded-lg border bg-white flex items-center justify-between text-left outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-600 ${error ? "border-red-500" : "border-slate-300"}`}
          >
            <span className={value ? "text-slate-900" : "text-slate-400"}>{value || placeholder}</span>
            <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 bg-white w-[var(--radix-popover-trigger-width)]" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} className="text-base" data-testid={`${testId}-search`} />
            <CommandList>
              <CommandEmpty>Sin resultados.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem key={opt} value={opt} onSelect={() => { onChange(opt); setOpen(false); }} data-testid={`${testId}-opt-${opt}`} className="text-base py-3 cursor-pointer">
                    <Check className={`mr-2 h-4 w-4 ${value === opt ? "opacity-100 text-orange-600" : "opacity-0"}`} />
                    {opt}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ErrorText error={error} />
    </div>
  );
}
